import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiUser } = FiIcons;

const Testimonials = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const testimonials = [
    {
      name: 'Sarah & Michael Thompson',
      country: 'United Kingdom',
      rating: 5,
      text: 'Hotel Athgira exceeded our expectations! The location right opposite the Elephant Transit Home is unbeatable. We could walk over in the morning to see the baby elephants being fed. The rooms were spotless, AC worked perfectly after our hot safari days, and the Niluka Safari guides were incredibly knowledgeable.',
      highlight: 'Perfect location & expert guides'
    },
    {
      name: 'Raj Patel Family',
      country: 'India',
      rating: 5,
      text: 'We stayed here with our two children and it was the perfect safari basecamp. The family room was spacious, the pool was a lifesaver for cooling off, and the staff went above and beyond to help us plan our safaris. The early morning departure was seamless - they had everything organized.',
      highlight: 'Excellent for families'
    },
    {
      name: 'Emma & James Wilson',
      country: 'Australia',
      rating: 5,
      text: 'As photography enthusiasts, we appreciated how the Niluka Safari guides positioned us for the best shots. The hotel staff even cleaned our camera equipment after dusty safari drives! The restaurant served excellent local food, and we loved watching elephants from our room balcony.',
      highlight: 'Photographer-friendly service'
    },
    {
      name: 'Klaus & Ingrid Mueller',
      country: 'Germany',
      rating: 5,
      text: 'We have stayed at many safari lodges across Africa and Asia, but Hotel Athgira stands out for its authentic hospitality. No pretentious luxury - just genuine care, clean comfortable rooms, and the best safari access we have experienced. Will definitely return!',
      highlight: 'Authentic safari hospitality'
    },
    {
      name: 'Chen Wei & Li Ming',
      country: 'Singapore',
      rating: 5,
      text: 'The Ranger Package was excellent value - room, breakfast, and safari all included. The guides spoke good English and were patient with our questions. Hotel facilities were exactly what we needed after long safari days. The location saved us so much travel time to the park.',
      highlight: 'Great value Ranger Package'
    },
    {
      name: 'David & Lisa Rodriguez',
      country: 'Canada',
      rating: 5,
      text: 'Hotel Athgira is exactly what a safari hotel should be - functional, comfortable, and perfectly located. The staff are safari experts, not just hotel workers. They helped us spot elephant families we would have missed otherwise. The pool and restaurant were perfect for relaxing between game drives.',
      highlight: 'Staff are safari experts'
    }
  ];

  const stats = [
    { value: '9.4', label: 'Guest Rating', sublabel: 'Based on 500+ reviews' },
    { value: '96%', label: 'Recommend Rate', sublabel: 'Would stay again' },
    { value: '15+', label: 'Years Experience', sublabel: 'Safari hospitality' },
    { value: '100%', label: 'Elephant Sightings', sublabel: 'Guaranteed or refund' }
  ];

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Guest <span className="gradient-text">Stories</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
            Hear from fellow safari enthusiasts who have made Hotel Athgira their basecamp 
            for unforgettable Udawalawe experiences.
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid md:grid-cols-4 gap-8 mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              className="glass-effect rounded-2xl p-6 text-center hover-lift"
            >
              <div className="text-4xl font-bold gradient-text mb-2">{stat.value}</div>
              <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
              <div className="text-gray-600 text-sm font-inter">{stat.sublabel}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
              className="glass-effect rounded-3xl p-6 hover-lift"
            >
              {/* Header */}
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 liquid-gradient rounded-full flex items-center justify-center mr-4">
                  <SafeIcon icon={FiUser} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 font-inter">{testimonial.name}</h4>
                  <p className="text-gray-600 text-sm">{testimonial.country}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-orange-500 fill-current" />
                ))}
                <span className="ml-2 text-sm text-gray-600 font-inter">5.0/5.0</span>
              </div>

              {/* Highlight */}
              <div className="bg-orange-50 rounded-lg px-3 py-2 mb-4">
                <span className="text-orange-700 font-semibold text-sm">{testimonial.highlight}</span>
              </div>

              {/* Review Text */}
              <p className="text-gray-700 font-inter text-sm leading-relaxed">
                "{testimonial.text}"
              </p>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 text-center"
        >
          <div className="liquid-gradient rounded-3xl p-8 text-white">
            <h3 className="text-3xl font-playfair font-bold mb-4">
              Join Our Community of Safari Enthusiasts
            </h3>
            <p className="text-xl mb-6 opacity-90 max-w-2xl mx-auto">
              Experience the same exceptional hospitality and expert safari guidance 
              that has earned us these glowing reviews from guests worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                className="bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover-lift"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Book Your Safari Adventure
              </motion.button>
              <motion.button
                className="glass-effect-dark border border-white/20 text-white px-8 py-4 rounded-full font-semibold hover-lift"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Read More Reviews
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;